/**
 * Mapa interactivo de la zona del delegado — Iter277.
 *
 * Pide las coordenadas geocodificadas de TODAS las oportunidades de la zona
 * (`GET /delegado/oportunidades/mapa`) y las proyecta sobre un lienzo simple
 * (sin dependencia de Leaflet/Google Maps JS SDK — sólo bbox lineal de
 * España) para poder ver de un vistazo toda la cartera de la zona a la vez.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, MapPin } from "lucide-react";
import { API } from "@/context/AuthContext";

// bbox aproximado de España peninsular (lat/lng) usado para proyectar los pines.
const BBOX = { latMin: 36.0, latMax: 43.8, lngMin: -9.5, lngMax: 3.5 };
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

function project(lat, lng) {
  const left = clamp(((lng - BBOX.lngMin) / (BBOX.lngMax - BBOX.lngMin)) * 100, 1, 99);
  const top = clamp((1 - (lat - BBOX.latMin) / (BBOX.latMax - BBOX.latMin)) * 100, 1, 99);
  return { left: `${left}%`, top: `${top}%` };
}

const scoreColor = (s) => (s >= 90 ? "bg-rose-500" : s >= 80 ? "bg-amber-500" : "bg-emerald-500");
const fmtEur = (n) =>
  n == null ? "—" :
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

export default function MapaZonaDelegado({ onSelectPin }) {
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/delegado/oportunidades/mapa`);
      setPins(r.data.pins || []);
    } catch { toast.error("No se pudo cargar el mapa de la zona"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-center py-16 text-slate-400" data-testid="delegado-mapa-loading"><Loader2 className="animate-spin mx-auto mb-2" size={24} />Cargando mapa de tu zona…</div>;

  return (
    <div data-testid="delegado-mapa-zona-content">
      <p className="text-xs text-slate-500 mb-3">{pins.length} oportunidad{pins.length === 1 ? "" : "es"} geolocalizada{pins.length === 1 ? "" : "s"} en tu zona.</p>
      <div className="relative w-full bg-gradient-to-br from-emerald-50 to-slate-50 border border-slate-200 rounded-xl overflow-hidden" style={{ paddingTop: "70%" }} data-testid="delegado-mapa-canvas">
        {pins.map((p) => {
          const pos = project(p.lat, p.lng);
          return (
            <button
              key={p.external_id}
              style={{ position: "absolute", left: pos.left, top: pos.top, transform: "translate(-50%, -100%)" }}
              className="group z-10"
              data-testid={`delegado-mapa-pin-${p.external_id}`}
              onMouseEnter={() => setHovered(p.external_id)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => onSelectPin?.(p.external_id)}
            >
              <MapPin size={22} className={`${scoreColor(p.ai_score)} text-white rounded-full p-0.5 drop-shadow-md hover:scale-125 transition-transform`} fill="currentColor" />
              {hovered === p.external_id && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg p-2.5 text-left z-20">
                  <p className="text-xs font-semibold text-slate-800 line-clamp-2">{p.title}</p>
                  <p className="text-[11px] text-slate-500 mt-1">{p.municipality} · Score {p.ai_score}</p>
                  <p className="text-[11px] font-semibold text-[#007934]">{fmtEur(p.value_eur)}</p>
                </div>
              )}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-3 text-[11px] text-slate-500">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-rose-500 inline-block" /> Score ≥ 90</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-500 inline-block" /> Score ≥ 80</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" /> Score &lt; 80</span>
      </div>
    </div>
  );
}
